import { toFunctionSelector, toEventSelector, parseAbi, decodeEventLog } from 'viem';

// src/abi/types.ts
function emptyNormalizedAbi() {
  return { entries: [], reads: [], writes: [], events: [], errors: [], selectorIndex: {}, topicIndex: {} };
}
function paramType(p) {
  if (p.type.startsWith("tuple")) {
    const components = p.components ?? [];
    const inner = components.map(paramType).join(",");
    const suffix = p.type.slice("tuple".length);
    return `(${inner})${suffix}`;
  }
  return p.type;
}
function functionSignature(fn) {
  return `${fn.name}(${(fn.inputs ?? []).map(paramType).join(",")})`;
}
function eventSignature(ev) {
  return `${ev.name}(${(ev.inputs ?? []).map(paramType).join(",")})`;
}
function errorSignature(err) {
  return `${err.name}(${(err.inputs ?? []).map(paramType).join(",")})`;
}
function functionSelector(fn) {
  return toFunctionSelector(functionSignature(fn));
}
function eventTopic(ev) {
  return toEventSelector(eventSignature(ev));
}
var byName = (a, b) => (a.name ?? "").localeCompare(b.name ?? "");
function isReadFunction(fn) {
  if (fn.stateMutability === "view" || fn.stateMutability === "pure") return true;
  if (fn.stateMutability !== void 0) return false;
  return fn.constant === true;
}
function normalizeAbi(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return emptyNormalizedAbi();
  const reads = [];
  const writes = [];
  const events = [];
  const errors = [];
  const selectorIndex = {};
  const topicIndex = {};
  for (const item of entries) {
    if (item.type === "function") {
      const fn = item;
      if (isReadFunction(fn)) reads.push(fn);
      else writes.push(fn);
      try {
        selectorIndex[functionSelector(fn)] = fn;
      } catch {
      }
    } else if (item.type === "event") {
      const ev = item;
      events.push(ev);
      try {
        topicIndex[eventTopic(ev)] = ev;
      } catch {
      }
    } else if (item.type === "error") {
      errors.push(item);
    }
  }
  reads.sort(byName);
  writes.sort(byName);
  events.sort(byName);
  errors.sort(byName);
  return { entries, reads, writes, events, errors, selectorIndex, topicIndex };
}
var isNamed = (item) => typeof item.name === "string" && item.name.trim().length > 0;
function usableFragmentCount(abi) {
  return Object.values(abi.selectorIndex).filter(isNamed).length + Object.values(abi.topicIndex).filter(isNamed).length + abi.errors.filter(isNamed).length;
}
function itemKind(item) {
  if (item === null) return "null";
  if (Array.isArray(item)) return "a nested array";
  if (typeof item !== "object") return `a bare ${typeof item}`;
  const type = item.type;
  return typeof type === "string" && type.trim() ? `"${type.trim()}"` : 'an item with no "type"';
}
function describeAbiShortfall(abi) {
  if (usableFragmentCount(abi) > 0) return null;
  const total = abi.entries.length;
  if (total === 0) {
    return "This ABI is an empty array \u2014 there is nothing to decode with.";
  }
  const declared = abi.reads.length + abi.writes.length + abi.events.length;
  if (declared > 0) {
    return `Every one of the ${declared} function/event ${declared === 1 ? "item" : "items"} here is malformed \u2014 none of them yields both a name and a selector, so this ABI cannot decode anything. Check each item's \`name\` and the types in its \`inputs\`.`;
  }
  const kinds = [...new Set(abi.entries.slice(0, 20).map(itemKind))].sort();
  return `This array has ${total} ${total === 1 ? "item" : "items"} but no usable function, event or error (found ${kinds.join(
    ", "
  )}). Paste the contract's ABI array itself \u2014 not a build artifact or a response envelope with the array nested inside it.`;
}
function parseAbiJson(text) {
  const trimmed = text.trim();
  if (!trimmed) return { abi: null, error: new Error("ABI is empty.") };
  try {
    const parsed = JSON.parse(trimmed);
    const entries = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.abi) ? parsed.abi : null;
    if (!entries) return { abi: null, error: new Error("ABI must be a JSON array of items.") };
    const abi = normalizeAbi(entries);
    const shortfall = describeAbiShortfall(abi);
    if (shortfall) return { abi: null, error: new Error(shortfall) };
    return { abi, error: null };
  } catch (err) {
    return { abi: null, error: err instanceof Error ? err : new Error(String(err)) };
  }
}
function parseHumanAbi(signatures) {
  const lines = signatures.map((s) => s.trim()).filter(Boolean);
  if (lines.length === 0) return { abi: null, error: new Error("No signatures provided.") };
  try {
    const entries = parseAbi(lines);
    return { abi: normalizeAbi(entries), error: null };
  } catch (err) {
    return { abi: null, error: err instanceof Error ? err : new Error(String(err)) };
  }
}
function abiMetaFromDto(dto) {
  return {
    chainId: dto.chain_id,
    address: dto.address,
    contractName: dto.contract_name ?? null,
    compilerVersion: dto.compiler_version ?? null,
    isProxy: dto.is_proxy,
    implementationAddresses: dto.implementation_addresses,
    source: dto.source ?? null,
    fetchedFrom: dto.fetched_from ?? null,
    confidence: dto.confidence,
    evidence: dto.evidence,
    explanation: dto.explanation,
    lifecycle: typeof dto.lifecycle === "string" ? dto.lifecycle : void 0,
    isHumanApproved: dto.is_human_approved,
    orgId: dto.org_id,
    tenantId: dto.tenant_id,
    updatedAt: dto.updated_at ?? null
  };
}
function normalizedAbiFromDto(dto) {
  return normalizeAbi(dto.entries ?? []);
}
function abiToJson(abi) {
  return JSON.stringify(abi.entries, null, 2);
}

// src/addresses/model.ts
var ADDRESS_FIELD_DEFAULTS = {
  address_type: "unknown",
  risk_level: "safe",
  severity: "info",
  lifecycle: "candidate",
  is_primary_chain: false,
  is_human_approved: false,
  system_default: false,
  confidence: 1
};
function isDefaultedField(row, field) {
  const value = row?.[field];
  if (value === null || value === void 0) return true;
  const fallback = ADDRESS_FIELD_DEFAULTS[field];
  return value === fallback;
}

// src/evidence/confidence.ts
function recordedConfidence(row) {
  const score = row?.confidence;
  if (typeof score !== "number" || !Number.isFinite(score) || score < 0 || score > 1) return null;
  if (row?.confidence_status === "unscored" || row?.confidence_scored === false) return null;
  const evidence = row?.evidence;
  const hasEvidence = Array.isArray(evidence) ? evidence.length > 0 : !!evidence && typeof evidence === "object" && Object.keys(evidence).length > 0;
  return row?.confidence_status === "scored" || row?.confidence_scored === true || score !== 1 || hasEvidence ? score : null;
}

// src/abi/confidence.ts
function recordedAbiConfidence(meta) {
  return recordedConfidence({
    confidence: meta?.confidence,
    confidence_status: meta?.confidenceStatus,
    confidence_scored: meta?.confidenceScored,
    evidence: meta?.evidence?.length ? meta.evidence : meta?.explanation?.filter((line) => line.trim())
  });
}
function abiConfidenceIsRecorded(meta) {
  return recordedAbiConfidence(meta) !== null;
}
function describeUnscoredAbi(meta) {
  if (abiConfidenceIsRecorded(meta)) return null;
  const defaulted = meta != null && meta.confidence != null && isDefaultedField({ confidence: meta.confidence }, "confidence");
  return defaulted ? "Not scored \u2014 this ABI was stored with the default confidence and no evidence behind it, so there is no score to report." : "Not scored \u2014 no confidence is recorded for this ABI.";
}

// src/abi/merge.ts
function itemKey(item) {
  if (item.type === "function") return `function:${functionSignature(item)}`;
  if (item.type === "event") return `event:${eventSignature(item)}`;
  if (item.type === "error") return `error:${errorSignature(item)}`;
  return `${item.type}`;
}
function mergeNormalizedAbis(abis) {
  const seen = /* @__PURE__ */ new Set();
  const entries = [];
  for (const n of abis) {
    for (const item of n.entries) {
      const key = itemKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      entries.push(item);
    }
  }
  return normalizeAbi(entries);
}
function signatureSet(abi) {
  const out = /* @__PURE__ */ new Set();
  for (const fn of [...abi.reads, ...abi.writes]) out.add(`function:${functionSignature(fn)}`);
  for (const ev of abi.events) out.add(`event:${eventSignature(ev)}`);
  for (const err of abi.errors) out.add(`error:${errorSignature(err)}`);
  return out;
}
function diffAbis(a, b) {
  const sa = signatureSet(a);
  const sb = signatureSet(b);
  const onlyA = [...sa].filter((s) => !sb.has(s)).sort();
  const onlyB = [...sb].filter((s) => !sa.has(s)).sort();
  const common = [...sa].filter((s) => sb.has(s)).sort();
  return { onlyA, onlyB, common };
}

// src/abi/defaults.ts
var ERC20_SIGNATURES = [
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function transferFrom(address from, address to, uint256 amount) returns (bool)",
  "event Transfer(address indexed from, address indexed to, uint256 value)",
  "event Approval(address indexed owner, address indexed spender, uint256 value)"
];
var ERC721_EXTRA_SIGNATURES = [
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function tokenURI(uint256 tokenId) view returns (string)",
  "function getApproved(uint256 tokenId) view returns (address)",
  "function isApprovedForAll(address owner, address operator) view returns (bool)",
  "function setApprovalForAll(address operator, bool approved)",
  "function safeTransferFrom(address from, address to, uint256 tokenId)"
];
var DEFAULT_ERC_SIGNATURES = [...ERC20_SIGNATURES, ...ERC721_EXTRA_SIGNATURES];
var cached = null;
function defaultErcAbi() {
  if (cached) return cached;
  const { abi } = parseHumanAbi(DEFAULT_ERC_SIGNATURES);
  cached = abi ?? emptyNormalizedAbi();
  return cached;
}
function coerce(abi) {
  return "topicIndex" in abi ? abi : normalizeAbi(abi);
}
function decodeLog(log, abi) {
  const normalized = coerce(abi);
  const topic0 = log.topics[0];
  const base = {
    address: log.address,
    topic0,
    blockNumber: log.blockNumber ?? null,
    logIndex: log.logIndex ?? null,
    transactionHash: log.transactionHash ?? null,
    decoded: false
  };
  if (!topic0) return { ...base, reason: "anonymous event (no topic0)" };
  const known = normalized.topicIndex[topic0];
  try {
    const result = decodeEventLog({
      abi: normalized.entries,
      data: log.data,
      topics: log.topics
    });
    return {
      ...base,
      eventName: result.eventName,
      args: result.args ?? void 0,
      decoded: true
    };
  } catch (error) {
    return {
      ...base,
      eventName: known?.name,
      reason: known ? errorMessage(error) : "unknown event (topic0 not in ABI)"
    };
  }
}
function decodeLogs(logs, abi) {
  const normalized = coerce(abi);
  return logs.map((log) => decodeLog(log, normalized));
}
function summarizeLogs(logs) {
  const counts = /* @__PURE__ */ new Map();
  for (const log of logs) {
    const name = log.eventName ?? "(unknown)";
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}
function errorMessage(error) {
  if (error instanceof Error) return error.message.split("\n")[0] ?? "decode failed";
  return "decode failed";
}

export { DEFAULT_ERC_SIGNATURES, abiConfidenceIsRecorded, abiMetaFromDto, abiToJson, decodeLog, decodeLogs, defaultErcAbi, describeAbiShortfall, describeUnscoredAbi, diffAbis, emptyNormalizedAbi, errorSignature, eventSignature, eventTopic, functionSelector, functionSignature, isReadFunction, itemKey, mergeNormalizedAbis, normalizeAbi, normalizedAbiFromDto, parseAbiJson, parseHumanAbi, recordedAbiConfidence, signatureSet, summarizeLogs, usableFragmentCount };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map